import { z } from 'zod'
import { BUSINESS_NAME_MAX_LENGTH, REGISTRATION_NUMBER_MAX_LENGTH } from '../validation/business'
import { isVatId, normalizeVatId } from './billing-entity'

export const businessSchema = z.object({
  id: z.string().uuid(),
  companyName: z.string(),
  legalName: z.string(),
  registrationNumber: z.string().nullable(),
  vatId: z.string().nullable(),
  /** True from a short sign-up until the official name is given. */
  companyDetailsPending: z.boolean(),
  /** Whether the business has a place, an organizer page or an event; false right after sign-up. */
  hasProfile: z.boolean(),
  stripeCustomerId: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  subscriptionStatus: z.string(),
  subscriptionTier: z.string(),
  stripeTrialEndsAt: z.string().nullable(),
  stripeScheduledChangeAt: z.string().nullable(),
  stripeScheduledChangeTargetTier: z.string().nullable(),
  businessOwnerId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const businessProfileSchema = z.object({
  id: z.string(),
  authUserId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  businessId: z.string().uuid(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const businessMeResponseSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    roles: z.array(z.string()),
  }),
  profile: businessProfileSchema,
  business: businessSchema.nullable(),
})

export const businessMeQuerySchema = z.object({
  businessId: z.string().uuid().optional(),
})

const clearableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => value || null)
    .optional()

/**
 * Changes to the company details. A field left out stays as it is; an empty register number or
 * VAT number clears it. Giving the official name completes the details after a short sign-up.
 */
export const companyDetailsUpdateSchema = z.object({
  companyName: z.string().trim().min(1).max(BUSINESS_NAME_MAX_LENGTH).optional(),
  legalName: z.string().trim().min(1).max(BUSINESS_NAME_MAX_LENGTH).optional(),
  registrationNumber: clearableText(REGISTRATION_NUMBER_MAX_LENGTH),
  vatId: z
    .string()
    .nullable()
    .transform(normalizeVatId)
    .refine(isVatId, 'Invalid VAT number')
    .optional(),
})

export type Business = z.infer<typeof businessSchema>
export type BusinessProfile = z.infer<typeof businessProfileSchema>
export type BusinessMeResponse = z.infer<typeof businessMeResponseSchema>
export type CompanyDetailsUpdate = z.infer<typeof companyDetailsUpdateSchema>
